import { useState, useEffect, useCallback } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import type { SetupIntent, Appearance } from '@stripe/stripe-js'
import { getStripe } from '@/lib/stripe'
import { supabase } from '@/lib/supabase'
import { PaymentForm } from './PaymentForm'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react'

type CheckoutStep = 'loading' | 'payment' | 'creating' | 'success' | 'error'

interface SubscriptionCheckoutProps {
  onSuccess?: () => void
}

// Appearance theme for Stripe Elements
const appearance: Appearance = {
  theme: 'stripe',
  variables: {
    colorPrimary: '#0f172a',
    colorBackground: '#ffffff',
    colorText: '#1e293b',
    colorDanger: '#ef4444',
    fontFamily: 'system-ui, sans-serif',
    spacingUnit: '4px',
    borderRadius: '8px',
  },
}

export function SubscriptionCheckout({ onSuccess }: SubscriptionCheckoutProps) {
  const [step, setStep] = useState<CheckoutStep>('loading')
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [stripePromise] = useState(() => getStripe())

  // Initialize SetupIntent on mount
  const initializeSetup = useCallback(async () => {
    try {
      setStep('loading')
      setError(null)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-setup-intent`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
        }
      )

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to initialize payment')
      }

      const data = await response.json()
      setClientSecret(data.client_secret)
      setStep('payment')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(message)
      setStep('error')
    }
  }, [])

  useEffect(() => {
    initializeSetup()
  }, [initializeSetup])

  // Handle successful payment method setup
  const handlePaymentSuccess = async (setupIntent: SetupIntent) => {
    try {
      setStep('creating')
      setError(null)

      // Extract payment method from setupIntent
      const paymentMethodId = setupIntent.payment_method
      if (!paymentMethodId) {
        throw new Error('No payment method found')
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        throw new Error('Not authenticated')
      }

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-subscription`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ payment_method_id: paymentMethodId }),
        }
      )

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create subscription')
      }

      // Handle 3D Secure if required
      if (data.requires_action && data.payment_intent_client_secret) {
        const stripe = await getStripe()
        if (!stripe) {
          throw new Error('Stripe not loaded')
        }

        const { error: confirmError } = await stripe.confirmCardPayment(
          data.payment_intent_client_secret
        )

        if (confirmError) {
          throw new Error(confirmError.message || 'Authentication failed')
        }
      }

      // Success!
      setStep('success')
      onSuccess?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred'
      setError(message)
      setStep('error')
    }
  }

  const handleRetry = () => {
    setError(null)
    initializeSetup()
  }

  // Loading state
  if (step === 'loading') {
    return (
      <div className="space-y-4">
        <div className="rounded-lg border p-4">
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-12 w-full mb-4" />
          <Skeleton className="h-12 w-full" />
        </div>
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  // Error state
  if (step === 'error') {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Payment Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button onClick={handleRetry} className="w-full">
          Try Again
        </Button>
      </div>
    )
  }

  // Creating subscription state
  if (step === 'creating') {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Activating your subscription...</p>
      </div>
    )
  }

  // Success state
  if (step === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-4">
        <div className="rounded-full bg-green-100 p-3">
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        </div>
        <h3 className="text-xl font-semibold">Subscription Active!</h3>
        <p className="text-center text-muted-foreground">
          Your subscription has been activated successfully. You now have full access to all features.
        </p>
        <Button onClick={onSuccess} className="mt-4">
          Continue
        </Button>
      </div>
    )
  }

  // Payment form state
  if (!clientSecret) {
    return null
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-4">Payment Details</h3>

      <Elements
        stripe={stripePromise}
        options={{
          clientSecret,
          appearance,
        }}
      >
        <PaymentForm onSuccess={handlePaymentSuccess} />
      </Elements>
    </div>
  )
}
